import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const HOURS = ['07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00'];

const CYCLE_COLORS = {
  'Post-Élémentaire': { bg: '#ede9fe', color: '#7c3aed', border: '#ddd6fe' },
  'Élémentaire':      { bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' },
  'Pré-Scolaire':     { bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
  'Secondaire':       { bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0' },
};

function detectCycle(session) {
  const name = session?.class?.level?.cycle?.name || session?.class?.name || '';
  if (name.includes('Post') || name.includes('Collège')) return 'Post-Élémentaire';
  if (name.includes('Élémentaire') || name.includes('CM') || name.includes('CE') || name.includes('CP') || name.includes('CI')) return 'Élémentaire';
  if (name.includes('PS') || name.includes('MS') || name.includes('GS') || name.includes('Pré')) return 'Pré-Scolaire';
  if (name.includes('2nde') || name.includes('1ère') || name.includes('Tle') || name.includes('Secondaire')) return 'Secondaire';
  return null;
}

export default function TimetableGrid() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEnseignant = (user?.roles || []).includes('ENSEIGNANT');
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses]   = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classFilter, setClassFilter]   = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);
  const [flash, setFlash]       = useState('');

  function load() {
    setLoading(true);
    Promise.all([
      api.get('/timetable').catch(() => []),
      api.get('/catalog/classes').catch(() => []),
      api.get('/catalog/teachers').catch(() => []),
    ]).then(([s, c, t]) => {
      setSessions(s || []);
      setClasses(c || []);
      setTeachers(t || []);
      if (isEnseignant && user) {
        const ln = (user.lastName || '').trim().toLowerCase();
        const fn = (user.firstName || '').trim().toLowerCase();
        const me = (t || []).find((x) =>
          (x.lastName || '').trim().toLowerCase() === ln &&
          (x.firstName || '').trim().toLowerCase() === fn
        );
        if (me?.id) setTeacherFilter(me.id);
      }
    }).finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [user]);

  function showFlash(msg) { setFlash(msg); setTimeout(() => setFlash(''), 3000); }

  async function deleteSession(id) {
    try { await api.del(`/timetable/${id}`); load(); showFlash('Séance supprimée.'); }
    catch (e) { alert(e.message); }
  }

  // Filtrer les sessions
  const filtered = sessions.filter((s) => {
    if (classFilter && s.classId !== classFilter) return false;
    if (teacherFilter && s.teacherId !== teacherFilter) return false;
    return true;
  });

  // Construire la grille : day → hour → sessions
  const grid = {};
  DAYS.forEach((d) => { grid[d] = {}; HOURS.forEach((h) => { grid[d][h] = []; }); });
  filtered.forEach((s) => {
    const day = DAYS[s.dayOfWeek - 1] || DAYS[s.dayOfWeek];
    if (!day) return;
    const hour = s.startTime?.slice(0, 5);
    if (grid[day] && grid[day][hour] !== undefined) {
      grid[day][hour].push(s);
    }
  });

  // Stats
  const totalH = filtered.reduce((sum, s) => {
    if (!s.startTime || !s.endTime) return sum;
    const [sh, sm] = s.startTime.split(':').map(Number);
    const [eh, em] = s.endTime.split(':').map(Number);
    return sum + (eh * 60 + em - (sh * 60 + sm));
  }, 0);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: '1.15rem', fontWeight: 700 }}>Grille horaire</h1>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            {filtered.length} séance(s) · {Math.round(totalH / 60)}h{totalH % 60 > 0 ? String(totalH % 60).padStart(2, '0') : ''} total
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost btn-sm" onClick={() => navigate('/emploi-du-temps/horaire')}>📋 Horaire des cours</button>
          {!isEnseignant && <button className="btn btn-sm" onClick={() => setModal({})}>+ Ajouter une séance</button>}
        </div>
      </div>

      {flash && <div className="receipt-banner">{flash}</div>}

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}
          style={{ height: 32, fontSize: 12, border: '1px solid var(--line)', borderRadius: 6, padding: '0 8px', background: 'var(--surface)' }}>
          <option value="">Toutes les classes</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {!isEnseignant && (
          <select value={teacherFilter} onChange={(e) => setTeacherFilter(e.target.value)}
            style={{ height: 32, fontSize: 12, border: '1px solid var(--line)', borderRadius: 6, padding: '0 8px', background: 'var(--surface)' }}>
            <option value="">Tous les enseignants</option>
            {teachers.map((t) => <option key={t.id} value={t.id}>{t.lastName} {t.firstName}</option>)}
          </select>
        )}
        {(classFilter || (!isEnseignant && teacherFilter)) && (
          <button className="btn-ghost btn-sm" onClick={() => { setClassFilter(''); if (!isEnseignant) setTeacherFilter(''); }}>✕ Effacer</button>
        )}
      </div>

      {/* Grille */}
      {loading ? <div className="loading">Chargement de la grille…</div> : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead>
              <tr style={{ background: 'var(--navy)' }}>
                <th style={{ padding: '10px 12px', fontSize: 11, color: 'rgba(255,255,255,.5)', fontWeight: 600, width: 72, borderRight: '1px solid rgba(255,255,255,.1)' }}>Heure</th>
                {DAYS.map((d) => (
                  <th key={d} style={{ padding: '10px 12px', fontSize: 12, color: '#fff', fontWeight: 600, textAlign: 'center', borderRight: '1px solid rgba(255,255,255,.1)' }}>
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {HOURS.map((hour, hi) => (
                <tr key={hour} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <td style={{ padding: '4px 12px', fontSize: 11, color: 'var(--muted-light)', fontFamily: 'monospace', verticalAlign: 'top', borderRight: '1px solid var(--line-soft)', background: 'var(--surface-soft)' }}>
                    {hour}
                  </td>
                  {DAYS.map((day) => {
                    const cells = grid[day]?.[hour] || [];
                    return (
                      <td key={day} style={{ padding: 4, verticalAlign: 'top', borderRight: '1px solid var(--line-soft)', minHeight: 48, height: 56 }}>
                        {cells.map((s) => {
                          const cycle = detectCycle(s);
                          const colors = CYCLE_COLORS[cycle] || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
                          return (
                            <div key={s.id}
                              style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '4px 6px', marginBottom: 2, cursor: 'pointer', position: 'relative' }}
                              onClick={() => setModal(s)}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: colors.color, lineHeight: 1.2 }}>
                                {s.subject?.name || '—'}
                              </div>
                              <div style={{ fontSize: 10, color: colors.color, opacity: .7, marginTop: 1 }}>
                                {s.class?.name || '—'}
                              </div>
                              <div style={{ fontSize: 9, color: colors.color, opacity: .6 }}>
                                {s.startTime?.slice(0,5)}–{s.endTime?.slice(0,5)}
                              </div>
                              {s.room?.name && <div style={{ fontSize: 9, color: colors.color, opacity: .6 }}>
                                📍 {s.room.name}
                              </div>}
                              {!isEnseignant && (
                              <button onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                                style={{ position: 'absolute', top: 2, right: 2, background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: colors.color, opacity: .5, padding: 0 }}>
                                ✕
                              </button>
                            )}
                            </div>
                          );
                        })}
                        {cells.length === 0 && (
                          <div style={{ height: '100%', minHeight: 48 }}
                            onDoubleClick={!isEnseignant ? () => setModal({ dayOfWeek: DAYS.indexOf(day) + 1, startTime: hour + ':00', endTime: (HOURS[hi + 1] || '18:00') + ':00' }) : undefined} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Légende */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
        {Object.entries(CYCLE_COLORS).map(([cycle, c]) => (
          <div key={cycle} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: c.bg, border: `1px solid ${c.border}` }} />
            <span style={{ color: 'var(--muted)' }}>{cycle}</span>
          </div>
        ))}
        <span style={{ fontSize: 11, color: 'var(--muted-light)', marginLeft: 8 }}>Double-clic sur une case vide pour ajouter</span>
      </div>

      {modal !== null && (
        <SessionModal
          initial={modal} classes={classes} teachers={teachers}
          subjects={[]} // chargé dans le modal
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); showFlash('Séance enregistrée ✓'); }}
        />
      )}
    </>
  );
}

// ── Modal Session EDT ─────────────────────────────────
function SessionModal({ initial, classes, teachers, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({
    dayOfWeek: initial.dayOfWeek || 1,
    startTime: initial.startTime?.slice(0, 5) || '08:00',
    endTime:   initial.endTime?.slice(0, 5)   || '09:00',
    classId:   initial.classId   || classes[0]?.id || '',
    subjectId: initial.subjectId || '',
    teacherId: initial.teacherId || teachers[0]?.id || '',
    roomId:    initial.roomId    || '',
  });
  const [subjects, setSubjects] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    api.get('/catalog/subjects').then(setSubjects).catch(() => {});
    api.get('/catalog/rooms').then(setRooms).catch(() => {});
  }, []);

  async function save() {
    if (!f.classId || !f.subjectId || !f.teacherId) { setError('Classe, matière et enseignant sont requis.'); return; }
    setSaving(true); setError('');
    try {
      const rest = f;
      const payload = { ...rest, dayOfWeek: Number(f.dayOfWeek) };
      if (isEdit) await api.patch(`/timetable/${initial.id}`, payload);
      else        await api.post('/timetable', payload);
      onSaved();
    } catch (e) { setError(e.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginBottom: 16, fontSize: '1rem', fontWeight: 700 }}>
          {isEdit ? 'Modifier la séance' : 'Nouvelle séance EDT'}
        </h2>
        {error && <div className="form-error">{error}</div>}

        <div className="field"><label>Jour *</label>
          <select value={f.dayOfWeek} onChange={set('dayOfWeek')}>
            {DAYS.map((d, i) => <option key={i} value={i + 1}>{d}</option>)}
          </select>
        </div>

        <div className="two-col">
          <div className="field"><label>Début *</label><input type="time" value={f.startTime} onChange={set('startTime')} /></div>
          <div className="field"><label>Fin *</label><input type="time" value={f.endTime} onChange={set('endTime')} /></div>
        </div>

        <div className="field"><label>Classe *</label>
          <select value={f.classId} onChange={set('classId')}>
            <option value="">— Sélectionner —</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="field"><label>Matière *</label>
          <select value={f.subjectId} onChange={set('subjectId')}>
            <option value="">— Sélectionner —</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div className="field"><label>Enseignant *</label>
          <select value={f.teacherId} onChange={set('teacherId')}>
            <option value="">— Sélectionner —</option>
            {teachers.map((t) => <option key={t.id} value={t.id}>{t.lastName} {t.firstName}</option>)}
          </select>
        </div>

        <div className="field"><label>Salle</label>
          <select value={f.roomId || ''} onChange={set('roomId')}><option value="">— Aucune —</option>{rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
            {saving ? 'Enregistrement…' : isEdit ? 'Modifier' : 'Ajouter'}
          </button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
